// src/services/reportFonts.js
//
// Font registry for the @react-pdf student/halaqa/school reports. Unlike the
// on-screen FontContext (which only flips CSS variables and can rely on
// system-installed faces via local()), a PDF must EMBED a real TTF — the
// reader has no access to the school's installed fonts. So every option here
// points at a concrete, CORS-enabled .ttf the PDF renderer can fetch.
//
// All faces are self-hosted TTFs in public/fonts/ (see services/pdfFonts.js for
// why the previous jsDelivr CDN URLs 404'd). "Palestine" is an optional custom
// face: drop Palestine.ttf into public/fonts/ to enable it. When the chosen
// face cannot be fetched, ensureReportFont() transparently falls back to Amiri
// so the report still renders rather than crashing the PDF pipeline.

import { Font } from '@react-pdf/renderer';
import { AMIRI_FONTS, AREF_FONTS } from './pdfFonts';

// Every face is a self-hosted TTF under public/fonts/ (served same-origin).
// @react-pdf cannot embed woff/woff2, and fontsource v5 dropped .ttf, so the
// old `cdn.jsdelivr.net/@fontsource/...ttf` URLs all 404'd — see pdfFonts.js.
// Each option: { key, labelAr, family, fonts: [{ src, fontWeight }], note? }
export const REPORT_FONT_OPTIONS = [
  {
    key: 'amiri',
    labelAr: 'أميري',
    family: 'Amiri',
    fonts: AMIRI_FONTS,
  },
  {
    key: 'arefRuqaa',
    labelAr: 'عارف رقعة',
    family: 'Aref Ruqaa',
    fonts: AREF_FONTS,
  },
  {
    key: 'palestine',
    labelAr: 'بالستاين',
    family: 'Palestine',
    // Local-only: requires Palestine.ttf in public/fonts/. Falls back to Amiri
    // automatically when the file is absent (ensureReportFont checks first).
    fonts: [
      { src: '/fonts/Palestine.ttf', fontWeight: 'normal' },
    ],
    note: 'يتطلب وضع ملف Palestine.ttf في public/fonts',
  },
];

export const DEFAULT_REPORT_FONT_KEY = 'amiri';
const STORAGE_KEY = 'bayan-report-font';

export const getReportFontOption = (key) =>
  REPORT_FONT_OPTIONS.find((o) => o.key === key) || REPORT_FONT_OPTIONS[0];

export const getStoredReportFontKey = () => {
  if (typeof window === 'undefined') return DEFAULT_REPORT_FONT_KEY;
  try {
    const k = localStorage.getItem(STORAGE_KEY);
    return REPORT_FONT_OPTIONS.some((o) => o.key === k) ? k : DEFAULT_REPORT_FONT_KEY;
  } catch {
    return DEFAULT_REPORT_FONT_KEY;
  }
};

export const setStoredReportFontKey = (key) => {
  if (!REPORT_FONT_OPTIONS.some((o) => o.key === key)) return;
  try { localStorage.setItem(STORAGE_KEY, key); } catch { /* ignore */ }
};

// Families already handed to Font.register this session — re-registering the
// same family throws in @react-pdf, so we guard.
const registered = new Set(['Amiri']); // Amiri is registered by the report doc.

const reachable = async (url) => {
  // Local (same-origin) assets: a SPA dev server returns index.html for a
  // missing file, so a 200 is not proof. Treat a non-ttf content-type as miss.
  try {
    const res = await fetch(url, { method: 'GET', headers: { Range: 'bytes=0-1' } });
    if (!res.ok) return false;
    const ct = (res.headers.get('content-type') || '').toLowerCase();
    if (ct.includes('text/html')) return false;
    return true;
  } catch {
    return false;
  }
};

/**
 * Ensure the requested report font is registered with @react-pdf and return
 * the resolved fontFamily string to stamp onto the document. Falls back to
 * 'Amiri' when the requested face cannot be fetched.
 */
export const ensureReportFont = async (fontKey) => {
  const opt = getReportFontOption(fontKey);
  if (!opt || opt.family === 'Amiri') return 'Amiri';
  if (registered.has(opt.family)) return opt.family;

  // Verify every source is fetchable before registering — a single bad URL
  // would otherwise make pdf().toBlob() reject.
  const checks = await Promise.all(opt.fonts.map((f) => reachable(f.src)));
  if (checks.some((ok) => !ok)) return 'Amiri';

  try {
    Font.register({ family: opt.family, fonts: opt.fonts });
    Font.registerHyphenationCallback((w) => [w]);
    registered.add(opt.family);
    return opt.family;
  } catch {
    return 'Amiri';
  }
};
